import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FileText, Download, Edit } from 'lucide-react';
import AIContentGenerator from '../../../services/aiService';

function TemplatesGallery() {
  const [isGenerating, setIsGenerating] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const templates = [
    { 
      title: "Music Artist Release", 
      tone: "Energetic • Pop/Hip-Hop",
      category: "entertainment",
      industry: "music",
      features: ["Headline + subhead scaffold", "Pull‑quote & boilerplate blocks", "Media contact & asset links"]
    },
    { 
      title: "Event Announcement", 
      tone: "Timely • Local Media",
      category: "events",
      industry: "events",
      features: ["Date/time formatting", "Venue details section", "Registration/ticket info"]
    },
    { 
      title: "Book/Product Launch", 
      tone: "Authoritative • SEO",
      category: "product",
      industry: "publishing",
      features: ["Product specifications", "Author/creator bio", "Availability information"]
    },
    { 
      title: "Crisis Response", 
      tone: "Calm • Transparent",
      category: "crisis",
      industry: "corporate",
      features: ["Acknowledgment statement", "Action plan outline", "Contact for inquiries"]
    },
    { 
      title: "Corporate Update", 
      tone: "Professional • B2B",
      category: "corporate",
      industry: "business",
      features: ["Executive quotes", "Financial data sections", "Strategic implications"]
    }
  ];

  const handleUseTemplate = async (template) => {
    setIsGenerating(true);
    setSelectedTemplate(template);
    
    try {
      const generatedContent = await AIContentGenerator?.generatePressRelease({
        topic: `${template?.title} Template`,
        company: 'Your Company Name',
        industry: template?.industry,
        keyPoints: template?.features,
        tone: template?.tone?.split(' • ')?.[0]?.toLowerCase()
      });
      
      // In a real app, you'd redirect to the editor with this template console.log('Generated template content:', generatedContent);
      
      // Simulate navigation to upload flow with template
      setTimeout(() => {
        setIsGenerating(false);
        setSelectedTemplate(null);
        alert(`${template?.title} template loaded! You can now customize it in the Upload section.`);
      }, 2000);
      
    } catch (error) {
      console.error('Error generating template:', error);
      setIsGenerating(false);
      setSelectedTemplate(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">Press Release Templates</h2>
        <div className="flex items-center gap-2">
          <button className="flex items-center gap-2 px-4 py-2 rounded-xl border border-gray-200 hover:bg-gray-50 transition">
            <Download size={16} />
            Export All
          </button>
          <button className="flex items-center gap-2 px-4 py-2 rounded-xl bg-black text-white hover:bg-gray-800 transition">
            <Edit size={16} />
            Create Custom
          </button>
        </div>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {templates?.map((template, index) => (
          <motion.div 
            key={index} 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white rounded-3xl p-6 border border-gray-100 shadow-sm h-full hover:shadow-md transition"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="h-12 w-12 rounded-2xl bg-gradient-to-r from-blue-500 to-purple-500 text-white grid place-items-center">
                <FileText size={24} />
              </div>
              <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-600">
                {template?.category}
              </span>
            </div>

            <h4 className="font-semibold mb-2 text-lg">{template?.title}</h4>
            <p className="text-sm text-gray-500 mb-4">{template?.tone}</p>

            <div className="mb-6">
              <h5 className="text-xs font-medium text-gray-700 mb-2">INCLUDES:</h5>
              <ul className="text-xs text-gray-600 space-y-1">
                {template?.features?.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <span className="text-green-500 mt-0.5">•</span>
                    {feature}
                  </li>
                ))}
              </ul>
            </div>

            <button 
              className={`w-full py-3 rounded-xl font-medium transition ${
                isGenerating && selectedTemplate?.title === template?.title
                  ? 'bg-gray-200 text-gray-500 cursor-not-allowed' :'bg-black text-white hover:bg-gray-800'
              }`}
              onClick={() => handleUseTemplate(template)}
              disabled={isGenerating && selectedTemplate?.title === template?.title}
            >
              {isGenerating && selectedTemplate?.title === template?.title 
                ? 'Generating Template...' :'Use Template'
              }
            </button>
          </motion.div>
        ))}
      </div>
      {/* Custom Template Builder Preview */}
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-3xl p-8 border border-purple-100"
      >
        <div className="text-center">
          <h3 className="text-xl font-bold mb-2">Need a Custom Template?</h3>
          <p className="text-gray-600 mb-4">
            Our AI can create a personalized press release template based on your specific industry, 
            tone, and requirements.
          </p>
          <button className="px-6 py-3 rounded-xl bg-purple-600 text-white font-medium hover:bg-purple-700 transition">
            Create AI Template
          </button>
        </div>
      </motion.div>
    </div>
  );
}

export default TemplatesGallery;